import * as React from 'react';
import {
  MdAdd,
  MdCheckCircle,
  MdClose,
  MdDelete,
  MdEdit,
  MdPauseCircle,
  MdSearch,
  MdVisibility,
} from 'react-icons/md';
import { GrTemplate } from 'react-icons/gr';
import DataGrid from '../../Components/Table/DataGrid';
import DeleteModel from '../../Components/Common/DeleteModel';
import { useNavigate } from "react-router-dom";
import { APPRoutes } from "../../Utils/Route";
import { useAuth } from '../../Context/AuthContext';
import { PermissionCodes, permissionHas, resolveUserPermissionCodes, isSuperAdminUser } from '../../Utils/Permissions';
import { toast } from 'react-toastify';
import { deleteEmailTemplate, getEmailTemplates } from '../../Services/EmailTemplateService';
import PageTitle from '../../Components/Common/PageTitle';
import SearchableDropdown from '../../Components/Subscription/SearchableDropdown';

const STATUS_OPTIONS = ['All', 'Active', 'Inactive'];
const STATUS_DROPDOWN_OPTIONS = STATUS_OPTIONS.map((option) => ({
  value: option,
  label: option,
}));
const STATUS_BADGE_STYLE = {
  Active: { backgroundColor: 'var(--hc-status-success-bg)', color: 'var(--hc-status-success-text)' },
  Inactive: { backgroundColor: 'var(--hc-status-inactive-bg)', color: 'var(--hc-status-inactive-text)' },
};

export default function EmailTemplatesList() {
  const navigate = useNavigate();
  const { token, permissions, decodedToken } = useAuth();
  const userPermissionCodes = React.useMemo(
    () => resolveUserPermissionCodes({ permissions, decodedToken }),
    [permissions, decodedToken],
  );

  const canViewEmailTemplates = permissionHas(userPermissionCodes, PermissionCodes.EMAIL_TEMPLATES_LIST, decodedToken);
  const canCreateEmailTemplate = permissionHas(userPermissionCodes, PermissionCodes.EMAIL_TEMPLATES_CREATE, decodedToken);
  const canEditEmailTemplate = permissionHas(userPermissionCodes, PermissionCodes.EMAIL_TEMPLATES_EDIT, decodedToken);
  const canDeleteEmailTemplate = permissionHas(userPermissionCodes, PermissionCodes.EMAIL_TEMPLATES_DELETE, decodedToken);

  const isSuperAdmin = isSuperAdminUser(decodedToken);

  const [search, setSearch] = React.useState('');
  const [status, setStatus] = React.useState('All');
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [deleteTarget, setDeleteTarget] = React.useState(null);
  const [deleteLoading, setDeleteLoading] = React.useState(false);
  const [deleteError, setDeleteError] = React.useState('');

  const handleCreateTemplate = React.useCallback(() => {
    navigate(APPRoutes.EMAIL_TEMPLATES_FORM);
  }, [navigate]);

  const loadEmailTemplates = React.useCallback(async () => {
    try {
      setLoading(true);
      const response = await getEmailTemplates(token);
      if (response?.is_success) {
        const accounts = response?.data?.Accounts ?? [];
        setRows(accounts);
      } else if (response?.status_code === 500) {
        toast.error("Internal Server Error. Please Contact Support Team.");
        setRows([]);
      } else {
        setRows([]);
      }
    } catch (error) {
      console.error(error);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [token]);

  React.useEffect(() => {
    if (token) {
      loadEmailTemplates();
    }
  }, [token, loadEmailTemplates]);

  const handleDeleteClick = React.useCallback((row) => {
    setDeleteError('');
    setDeleteTarget(row);
  }, []);

  const handleDeleteClose = React.useCallback(() => {
    if (deleteLoading) {
      return;
    }

    setDeleteTarget(null);
    setDeleteError('');
  }, [deleteLoading]);

  const handleDeleteConfirm = React.useCallback(async () => {
    if (!deleteTarget?.Id || !token) {
      return;
    }

    try {
      setDeleteLoading(true);
      setDeleteError('');
      const response = await deleteEmailTemplate(deleteTarget.Id, token);
      if (response?.is_success) {
        await loadEmailTemplates();
        setDeleteTarget(null);
        toast.success(response?.message || 'Template deleted successfully.');
      } else if (response?.status_code === 500) {
        toast.error("Internal Server Error. Please Contact Support Team.");
        setDeleteError('Internal Server Error. Please Contact Support Team.');
      } else {
        setDeleteError(response?.message || 'Failed to delete the template. Please try again.');
      }
      await loadEmailTemplates();
      setDeleteTarget(null);
    } catch (error) {
      console.error(error);
      setDeleteError('Something went wrong while deleting the template.');
    } finally {
      setDeleteLoading(false);
    }
  }, [deleteTarget, token, loadEmailTemplates]);

  const filteredRows = React.useMemo(() => {
    const term = search.trim().toLowerCase();

    return rows.filter((row) => {
      const matchesSearch =
        !term ||
        row.AccountName?.toLowerCase().includes(term);
      return matchesSearch;
    });
  }, [rows, search]);

  const stats = React.useMemo(() => {
    const totalTemplates = rows.reduce((sum, row) => sum + (row.TemplatesCount || 0), 0);

    return [
      {
        label: 'Total Accounts',
        value: rows.length,
        icon: <MdCheckCircle size={22} color="var(--hc-icon-blue-text)" />,
        iconBg: 'var(--hc-icon-blue-bg)',
        accentClass: 'border-primary',
        valueClass: 'text-primary',
      },
      {
        label: 'Total Templates',
        value: totalTemplates,
        icon: <GrTemplate size={22} color="var(--hc-status-success-text)" />,
        iconBg: 'var(--hc-icon-green-bg)',
        accentClass: 'border-success',
        valueClass: 'text-success',
      },
    ];
  }, [rows]);

  const columns = React.useMemo(() => {
    const cols = [];

    if (isSuperAdmin) {
      cols.push({
        id: 'AccountName',
        label: 'Client',
        minWidth: 180,
        format: (value) => value || 'N/A',
      });
    }

    cols.push(
      {
        id: 'TemplatesCount',
        label: 'Template Count',
        minWidth: 120,
        align: 'center',
        format: (value) => value ?? 0,
      },
      {
        id: 'StatusName',
        label: 'Status',
        minWidth: 100,
        align: 'center',
        format: (value) => (
          <span className="badge rounded-pill px-3 py-2" style={{
            backgroundColor: value === 'Active' ? 'var(--hc-status-success-bg)' : 'var(--hc-status-inactive-bg)',
            color: value === 'Active' ? 'var(--hc-status-success-text)' : 'var(--hc-status-inactive-text)',
          }}>
            {value || 'N/A'}
          </span>
        ),
      },
      {
        id: 'CreatedByName',
        label: 'CreatedBy',
        minWidth: 140,
        format: (value) => value || 'N/A',
      },
    );

    if (canEditEmailTemplate || canDeleteEmailTemplate) {
      cols.push({
        id: 'actions',
        label: 'Actions',
        minWidth: 80,
        align: 'center',
        format: (_value, row) => (
          <div className="d-flex align-items-center justify-content-center gap-2">
            {canEditEmailTemplate ? (
              <button
                type="button"
                title="Edit"
                className="btn btn-sm p-1 lh-1 hc-text-primary border-0 rounded-2"
                style={{ backgroundColor: 'var(--hc-icon-blue-bg)' }}
                onClick={() => {
                  const accountParam = row.AccountId !== undefined
                    ? row.AccountId === null
                      ? 'null'
                      : row.AccountId
                    : 'null';
                  navigate(
                    APPRoutes.EMAIL_TEMPLATES_EDIT
                      .replace(":accountId", accountParam)
                  );
                }}
              >
                <MdEdit size={16} />
              </button>
            ) : null}
            {canDeleteEmailTemplate ? (
              <button
                type="button"
                title="Delete"
                className="btn btn-sm p-1 lh-1 text-danger border-0 rounded-2"
                style={{ backgroundColor: 'var(--hc-status-danger-bg)' }}
                onClick={() => handleDeleteClick(row)}
              >
                <MdDelete size={16} />
              </button>
            ) : null}
          </div>
        ),
      });
    }

    return cols;
  }, [
    navigate,
    handleDeleteClick,
    canEditEmailTemplate,
    canDeleteEmailTemplate,
    isSuperAdmin,
  ]);

  return (
    <>
      <PageTitle title="Email Templates - Aegis 360"/>
      <div className="d-flex flex-md-row flex-column align-items-start justify-content-between mb-4">
        <div>
          <h4 className="fw-bold mb-1 hc-text-primary fs-3">Email Templates Management</h4>
          <p className="text-secondary mb-0 small">
            Manage all email templates across the Aegis 360 platform
          </p>
        </div>
        {canCreateEmailTemplate ? (
          <button
            onClick={handleCreateTemplate}
            type="button"
            className="btn hc-bg-primary mt-md-0 mt-2 w-md-auto btn-sm text-white fw-semibold d-flex align-items-center justify-content-center gap-1 px-3 py-2 rounded-3 text-nowrap"
          >
            <MdAdd size={20} />
            <span>Create Template</span>
          </button>
        ) : null}
      </div>

      {loading ? (
        <div className="row g-3 mb-3">
          {[1, 2].map((_, i) => (
            <div key={i} className="col-12 col-sm-6 col-lg">
              <div className="bg-white p-4 rounded-4 shadow">
                <div className="skeleton mb-2" style={{ height: 16, width: "60%" }} />
                <div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 28, width: "40%" }} />
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="row g-3 mb-3">
          {stats.map((stat) => (
            <div key={stat.label} className="col-12 col-sm-6 col-lg-4 col-xl">
              <div className="position-relative overflow-hidden bg-white p-4 rounded-4 shadow p-3 d-flex align-items-center gap-3 h-100 hc-list-kpi-card">
                <div
                  className={`position-absolute start-0 top-0 h-100 border-start border-5 ${stat.accentClass}`}
                />
                <div
                  className="d-inline-flex align-items-center justify-content-center rounded-3"
                  style={{ width: 44, height: 44, backgroundColor: stat.iconBg }}
                >
                  {stat.icon}
                </div>
                <div className="d-flex flex-column fw-bold lh-sm">
                  <span className="text-dark" style={{ fontSize: '0.85rem' }}>
                    {stat.label}
                  </span>
                  <span className={`fw-bold ${stat.valueClass} fs-5`}>
                    {stat.value}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="bg-white rounded-3 shadow-lg p-3 p-md-4">
        <div className="row g-2 mb-3">
          <div className="col-12 col-md-9">
            <div className="position-relative">
              <span
                className="position-absolute top-50 translate-middle-y d-inline-flex align-items-center justify-content-center text-secondary pe-none"
                style={{ left: '12px' }}
              >
                <MdSearch size={18} />
              </span>
              <input
                type="text"
                className="form-control form-control-sm hc-input"
                placeholder="Search Template By Client"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                style={{ paddingLeft: '36px', paddingRight: '36px', height: '40px' }}
              />
              {search ? (
                <button
                  type="button"
                  aria-label="Clear search"
                  onClick={() => setSearch('')}
                  className="btn btn-link bg-danger p-0 position-absolute top-50 translate-middle-y text-light rounded-circle"
                  style={{ right: '8px', lineHeight: 0 }}
                >
                  <MdClose size={18} />
                </button>
              ) : null}
            </div>
          </div>

          <div className="col-12 col-md-3">

               <SearchableDropdown
                  placeholder="Select status"
                  options={STATUS_DROPDOWN_OPTIONS}
                  value={status}
                  onChange={setStatus}
                  showSearch={true}
                />

            {/* <FormControl fullWidth size="small">
              <InputLabel id="template-status-label">Status</InputLabel>
              <Select
                labelId="template-status-label"
                label="Status"
                value={status}
                onChange={(event) => setStatus(event.target.value)}
              >
                {STATUS_OPTIONS.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl> */}
          </div>
        </div>

        <div className="bg-white p-3 rounded shadow">
          {loading ? (
            <div>
              {[...Array(8)].map((_, i) => (
                <div
                  key={i}
                  className="d-flex gap-3 align-items-center mb-3"
                >
                  <div className="skeleton position-relative overflow-hidden rounded-2 w-25" style={{ height: 16 }} />
                  <div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 16, width: "20%" }} />
                  <div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 16, width: "15%" }} />
                  <div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 16, width: "20%" }} />
                  <div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 16, width: "20%" }} />
                </div>
              ))}
            </div>
          ) : (
            <DataGrid
              columns={columns}
              rows={filteredRows}
              defaultRowsPerPage={10}
              stickyHeader
              getRowId={(row) => row.Id}
            />
          )}
        </div>
      </div>

      <DeleteModel
        isOpen={Boolean(deleteTarget)}
        title="Delete Email Template"
        entityLabel="template"
        itemName={deleteTarget?.TemplateName || ''}
        description="You are about to remove this email template from the platform. This action is permanent."
        confirmText="Delete Template"
        isLoading={deleteLoading}
        errorMessage={deleteError}
        onClose={handleDeleteClose}
        onConfirm={handleDeleteConfirm}
      />
    </>
  );
}

